import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import HoldsHeader from './HoldsHeader';
import HoldsTable from './HoldsTable';
import HoldModal from './HoldModal';
import HoldsKillsChart from './HoldsKillsChart';

const Holds = ({
  csrfToken,
  admissionSeatingChartEvent,
  addOnSeatingChartEvent,
  seatingChartEvents,
  holds,
  fetchHolds,
  createHold,
  updateHold,
  deleteHold,
  seatsIOPublicKey,
  seatChannels,
  reloadTicketTypes,
  reloadAddOns
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingHold, setEditingHold] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const isLegacy = selectedEvent?.legacy;
  const eventCloneMissing = !selectedEvent?.event_chart_clone_id;

  useEffect(() => {
    const available = [admissionSeatingChartEvent, addOnSeatingChartEvent, ...(seatingChartEvents || [])].filter(Boolean);
    if (!selectedEvent || !available.find(e => e.id === selectedEvent.id)) {
      setSelectedEvent(available[0] || null);
    }
  }, [admissionSeatingChartEvent?.id, addOnSeatingChartEvent?.id, (seatingChartEvents || []).map(e => e.id).join(',')]);

  useEffect(() => {
    if (!selectedEvent) return undefined;
    const controller = new AbortController();
    fetchHolds(csrfToken, selectedEvent, controller.signal);
    return () => controller.abort();
  }, [selectedEvent?.id]);

  const allEvents = [admissionSeatingChartEvent, addOnSeatingChartEvent, ...(seatingChartEvents || [])].filter(Boolean);

  if (!allEvents.length) {
    return (
      <div className="text-center my-5">Set up a seating chart to manage Holds.</div>
    );
  }

  if (isLegacy) {
    return (
      <div className="text-center my-5">Holds are not supported for legacy events.</div>
    );
  }

  if (eventCloneMissing) {
    return (
      <div className="text-center my-5">Holds are not supported for events without an event chart. Please reselect a chart to access Holds.</div>
    );
  }

  return (
    <div>
      <HoldsHeader
        events={allEvents}
        selectedEvent={selectedEvent}
        onSelectEvent={(value) => {
          const ev = allEvents.find(evt => String(evt.id) === String(value));
          setSelectedEvent(ev || null);
        }}
        onClickAdd={() => {
          setEditingHold(null);
          setShowModal(true);
        }}
      />
      <HoldsTable
        holds={holds}
        onEdit={(hold) => {
          setEditingHold(hold);
          setShowModal(true);
        }}
        onDelete={(hold) => {
          deleteHold(csrfToken, selectedEvent, hold).then(() => {
            if (selectedEvent?.context === 'admission') {
              reloadTicketTypes();
            } else {
              reloadAddOns();
            }
          });
        }}
      />
      <HoldsKillsChart selectedEvent={selectedEvent} />
      <HoldModal
        open={showModal}
        onClose={() => { setEditingHold(null); setShowModal(false); }}
        title={editingHold ? 'Edit Hold' : 'Add Hold'}
        seatsIOPublicKey={seatsIOPublicKey}
        channels={seatChannels}
        selectedEvent={selectedEvent}
        initialHold={editingHold}
        allowAreaPlaces={selectedEvent?.context === 'admission'}
        onSubmit={(payload) => {
          const action = editingHold
            ? updateHold(csrfToken, { id: editingHold.id, ...payload })
            : createHold(csrfToken, selectedEvent, payload);
          return action.then(() => {
            if (selectedEvent?.context === 'admission') {
              reloadTicketTypes();
            } else {
              reloadAddOns();
            }
            setEditingHold(null);
            setShowModal(false);
          });
        }}
      />
    </div>
  );
};

Holds.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  admissionSeatingChartEvent: PropTypes.object,
  addOnSeatingChartEvent: PropTypes.object,
  seatingChartEvents: PropTypes.array,
  holds: PropTypes.array.isRequired,
  fetchHolds: PropTypes.func.isRequired,
  createHold: PropTypes.func.isRequired,
  updateHold: PropTypes.func.isRequired,
  deleteHold: PropTypes.func.isRequired,
  seatsIOPublicKey: PropTypes.string.isRequired,
  seatChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
  reloadTicketTypes: PropTypes.func.isRequired,
  reloadAddOns: PropTypes.func.isRequired
};

export default Holds;
